/**
 * ScenarioPanel Component - Interactive scenario modeling with sliders and metrics
 * Requirements: 3.6, 3.7
 */

import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  initializeScenario,
  updateAdjustment,
  resetScenario,
  calculateScenarioMetrics,
} from '../store/slices/scenarioSlice';
import { CapacityRow, FormatAdjustment } from '../services/api';
import SliderControl from './SliderControl';
import ScenarioMetricsDisplay from './ScenarioMetricsDisplay';
import DraggableChart from './DraggableChart';

interface ScenarioPanelProps {
  siteId: string;
  capacity: CapacityRow[];
  fixedCost: number;
}

function ScenarioPanel({ siteId, capacity, fixedCost }: ScenarioPanelProps) {
  const dispatch = useAppDispatch();
  const { adjustments, calculatedMetrics, warnings, isModified, loading } = useAppSelector(
    (state) => state.scenario
  );

  // Initialize scenario when site changes
  useEffect(() => {
    const initialAdjustments: FormatAdjustment[] = capacity.map((row) => ({
      format: row.format,
      baselineValue: row.plannedCases,
      adjustedValue: row.plannedCases,
      delta: 0,
    }));
    dispatch(initializeScenario({ siteId, adjustments: initialAdjustments }));
  }, [dispatch, siteId, capacity]);

  // Calculate scenario when adjustments change
  useEffect(() => {
    if (isModified && adjustments.length > 0) {
      const timeoutId = setTimeout(() => {
        dispatch(calculateScenarioMetrics({ siteId, adjustments }));
      }, 300); // Debounce API calls
      return () => clearTimeout(timeoutId);
    }
  }, [dispatch, siteId, adjustments, isModified]);

  const handleSliderChange = useCallback(
    (format: string, value: number) => {
      dispatch(updateAdjustment({ format, adjustedValue: value }));
    },
    [dispatch]
  );

  const handleReset = useCallback(() => {
    dispatch(resetScenario());
  }, [dispatch]);

  // Calculate baseline metrics
  const baselineTotalCases = capacity.reduce((sum, row) => sum + row.plannedCases, 0);
  const baselineCostPerCase = baselineTotalCases > 0 ? fixedCost / baselineTotalCases : 0;
  const maxCapacity = capacity.reduce((sum, row) => sum + (row.loadCapacity || row.plannedCases), 0);
  const scenarioTotalCases = adjustments.reduce((sum, a) => sum + a.adjustedValue, 0);

  return (
    <div style={styles.container}>
      {/* Cost Curve Chart */}
      <div style={styles.chartSection}>
        <DraggableChart
          fixedCost={fixedCost}
          currentVolume={baselineTotalCases}
          maxVolume={maxCapacity}
          scenarioVolume={isModified ? scenarioTotalCases : undefined}
        />
      </div>

      {/* Sliders Section */}
      <div style={styles.slidersSection}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>Adjust Planned Cases</h3>
          <button
            onClick={handleReset}
            disabled={!isModified}
            style={{
              ...styles.resetButton,
              ...(isModified ? {} : styles.resetButtonDisabled),
            }}
          >
            Reset to Baseline
          </button>
        </div>

        <div style={styles.slidersList}>
          {capacity.map((row) => {
            const adjustment = adjustments.find((a) => a.format === row.format);
            const warning = warnings.find((w) => w.format === row.format);
            return (
              <SliderControl
                key={row.format}
                format={row.format}
                currentValue={adjustment?.adjustedValue ?? row.plannedCases}
                maxValue={row.loadCapacity ?? row.plannedCases * 1.5}
                baselineValue={row.plannedCases}
                onChange={(value) => handleSliderChange(row.format, value)}
                warning={warning?.message}
              />
            );
          })}
        </div>
      </div>

      {/* Metrics Section */}
      <div style={styles.metricsSection}>
        <h3 style={styles.sectionTitle}>Scenario Impact</h3>
        {loading ? (
          <div style={styles.loading}>Calculating...</div>
        ) : (
          <ScenarioMetricsDisplay
            metrics={calculatedMetrics}
            baselineCostPerCase={baselineCostPerCase}
            baselineTotalCases={baselineTotalCases}
            isModified={isModified}
          />
        )}
      </div>

      {/* Warnings Section */}
      {warnings.length > 0 && (
        <div style={styles.warningsSection}>
          <h4 style={styles.warningsTitle}>⚠️ Capacity Warnings</h4>
          {warnings.map((warning) => (
            <div key={warning.format} style={styles.warningItem}>
              <strong>{warning.format}:</strong> {warning.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  chartSection: {
    borderBottom: '1px solid #eee',
    paddingBottom: '1rem',
  },
  slidersSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '0.95rem',
    color: '#333',
    fontWeight: 600,
  },
  resetButton: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  resetButtonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  slidersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  metricsSection: {
    borderTop: '1px solid #eee',
    paddingTop: '1rem',
  },
  loading: {
    textAlign: 'center',
    padding: '1rem',
    color: '#666',
    fontStyle: 'italic',
  },
  warningsSection: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '6px',
    padding: '0.75rem',
  },
  warningsTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: '0.9rem',
    color: '#856404',
  },
  warningItem: {
    fontSize: '0.85rem',
    color: '#856404',
    marginBottom: '0.25rem',
  },
};

export default ScenarioPanel;
